import { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { fetchPuzzleTitles } from '../../api/puzzleService';
import { Container, ErrorText } from '../App';
import LoadingSpinner from '../shared/LoadingSpinner';
import { commonStyles } from '../../theme';

const DailyPuzzleListContainer = styled.div`
  padding-bottom: 20px;
`;

const PuzzleTitle = styled.h2`
  color: ${commonStyles.primaryText};
  margin-top: 10px;
  margin-bottom: 20px;
`;

const PuzzleUL = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const PuzzleItem = styled.li`
  padding: 0;
  margin-bottom: 16px;
  background-color: ${commonStyles.secondaryBackground};
  border: 1px solid ${commonStyles.primaryBorder};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;

  &:hover {
    background-color: ${commonStyles.primaryBackground};
    border-color: ${commonStyles.buttonPrimary};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const PuzzleContent = styled.div`
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 18px 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const PuzzleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const PuzzleStartingWord = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${commonStyles.primaryText};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const PuzzleDate = styled.div`
  font-size: 0.9rem;
  color: ${commonStyles.mutedText};
  font-weight: 500;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px 0;
  font-size: 1.1rem;
  color: ${commonStyles.mutedText};
`;


const DailyPuzzleList = ({ onSelectPuzzle }) => {
  const [puzzleTitles, setPuzzleTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef();

  const loadPuzzleTitles = useCallback(async (pageNum = 0, append = false) => {
    try {
      if (pageNum === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const response = await fetchPuzzleTitles(pageNum, 20);
      const newPuzzles = response.puzzles || response; // Handle different response formats
      
      if (append) {
        setPuzzleTitles(prev => [...prev, ...newPuzzles]);
      } else {
        setPuzzleTitles(newPuzzles);
      }
      
      // Check if we have more puzzles to load
      setHasMore(newPuzzles.length === 20);
      
    } catch (err) {
      setError('Failed to load puzzles');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Load more puzzles when reaching bottom
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadPuzzleTitles(nextPage, true);
    }
  }, [loadingMore, hasMore, page, loadPuzzleTitles]);

  // Intersection Observer for infinite scroll
  const lastPuzzleElementRef = useCallback(node => {
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        loadMore();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore, loadingMore, loadMore]);

  useEffect(() => {
    loadPuzzleTitles(0, false);
  }, [loadPuzzleTitles]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (error) {
    return <ErrorText>{error}</ErrorText>;
  }

  return (
    <Container>
      <DailyPuzzleListContainer>
        <PuzzleTitle>Daily Puzzles</PuzzleTitle>
        <PuzzleUL>
          {puzzleTitles.map((puzzle, index) => {
            const isLast = index === puzzleTitles.length - 1;
            return (
              <PuzzleItem 
                key={puzzle.id} 
                ref={isLast ? lastPuzzleElementRef : null}
                onClick={() => onSelectPuzzle(puzzle.date)}
              >
                <PuzzleContent>
                  <PuzzleInfo>
                    <PuzzleStartingWord>{puzzle.starting_word}</PuzzleStartingWord>
                    <PuzzleDate>{new Date(puzzle.date + 'T00:00:00').toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</PuzzleDate>
                  </PuzzleInfo>
                </PuzzleContent>
              </PuzzleItem>
            );
          })}
        </PuzzleUL>
        
        {loadingMore && <LoadingSpinner />}
        
        {!hasMore && puzzleTitles.length > 0 && (
          <LoadingMessage>-</LoadingMessage>
        )}
      </DailyPuzzleListContainer>
    </Container>
  );
};

export default DailyPuzzleList;